import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { ProductListItemModel } from 'autogen/swagger/all';
import { useProductListItemsByProductIdsQuery } from 'autogen/swagger/all/hooks';
import { QuestionAbout } from 'autogen/translation-keys/trans-website-comai';
import { Typography } from 'components/generic/typography';
import FlexColumn from 'components/Layout/components/Flex/components/FlexColumn';
import HorizontalProductItem from 'components/shared/productItem/components/HorizontalProductItem/HorizontalProductItem';
import format from 'helpers/translations';
import { verticalEllipsis } from 'modules/helpers/mixins';
import { UserMessageStyles } from '../shared/styles';
import type { UserMessage as UserMessageType } from '../shared/types';
import useComAIStore from '../store/comAIStore';

type UserMessageProps = {
    id: string;
};

const Message = styled.div`
    ${UserMessageStyles}
`;

const Wrapper = styled(FlexColumn)`
    opacity: 1;
    transform: translateY(0);
    transition: opacity 500ms ease-out, transform 500ms ease-out;

    @starting-style {
        opacity: 0;
        transform: translateY(10px);
    }
`;

const StyledProductName = styled(Typography.Body)`
    text-align: right;
    margin-left: auto;
    margin-bottom: ${({ theme }) => theme.Spacing4};
    ${verticalEllipsis(1)};
    white-space: nowrap;
`;

export default function UserMessage({ id }: UserMessageProps) {
    const { text, context } = useComAIStore((state) => state.chat[id]) as UserMessageType;
    const setProductListLoading = useComAIStore((state) => state.setProductListLoading);
    const { productCardId } = context || {};
    const { t } = useTranslation();

    const { data: productItemData, isFetching } = useProductListItemsByProductIdsQuery(
        {
            products: productCardId ? [Number(productCardId)] : []
        },
        { refetchOnWindowFocus: false, enabled: !!productCardId }
    );

    const productItemModel = productItemData?.find(
        (p) => p.$type === 'ProductListItemModel' && p.id === Number(productCardId)
    ) as ProductListItemModel;

    useEffect(() => {
        setProductListLoading(isFetching);
    }, [setProductListLoading, isFetching]);

    return (
        <Wrapper>
            {productItemModel && <HorizontalProductItem model={productItemModel} showRating />}
            <div>
                {context?.product && (
                    <StyledProductName strong>{format(t(QuestionAbout), context.product.name)}</StyledProductName>
                )}
                <Message>{text}</Message>
            </div>
        </Wrapper>
    );
}
